<!-- warehouse_scan/public/js/components/TypeSelector.vue -->
<template>
  <div class="type-selector">
    <!-- 主类型选择 -->
    <div class="type-grid">
      <div 
        v-for="(config, type) in typeConfig" 
        :key="type"
        class="type-card" 
        :class="{ active: modelType === type }"
        :style="{ borderColor: modelType === type ? config.color : 'transparent' }"
        @click="selectType(type)"
      >
        <div class="type-icon" :style="{ color: config.color }">{{ config.icon }}</div>
        <div class="type-title">{{ typeNames[type] }}</div>
        <div class="type-desc">{{ config.desc }}</div>
      </div>
    </div>

    <!-- 子类型选择 -->
    <div v-if="modelType && showSubtypes" class="subtype-bar">
      <div class="subtype-title">选择操作类型</div>
      <div class="subtype-buttons">
        <button
          v-for="sub in typeConfig[modelType].subtypes"
          :key="sub.code"
          class="subtype-btn"
          :class="{ active: modelSubtype === sub.code, disabled: sub.disabled }"
          :disabled="sub.disabled"
          :title="sub.disabled ? '该操作后端尚未实现' : ''"
          @click="selectSubtype(sub.code)"
        >
          <span class="subtype-icon">{{ sub.icon }}</span>
          <span class="subtype-name">{{ sub.name }}</span>
          <span v-if="sub.disabled" class="subtype-tag">未开放</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  type: {
    type: String,
    default: null
  },
  subtype: {
    type: String,
    default: null
  },
  showSubtypes: {
    type: Boolean,
    default: true
  }
});

const emit = defineEmits(['update:type', 'update:subtype', 'change']);

const modelType = computed({
  get: () => props.type,
  set: (val) => {
    emit('update:type', val);
    emit('change', val, null);
  }
});

const modelSubtype = computed({
  get: () => props.subtype,
  set: (val) => {
    emit('update:subtype', val);
    emit('change', props.type, val);
  }
});

const typeConfig = {
  inbound: {
    icon: '📥',
    color: '#28a745',
    desc: '采购入库 / 生产入库',
    subtypes: [
      { code: 'purchase', name: '采购入库', icon: '📦' },
      { code: 'production', name: '生产入库', icon: '🏭' },
      { code: 'return_in', name: '退货入库', icon: '↩️', disabled: true }
    ]
  },
  outbound: {
    icon: '📤',
    color: '#dc3545',
    desc: '销售出库 / 拣货出库 / 领料出库',
    subtypes: [
      { code: 'sales', name: '销售出库', icon: '🚚' },
      { code: 'delivery', name: '拣货出库', icon: '📋' },
      { code: 'issue', name: '领料出库', icon: '🔧' },
      { code: 'return_out', name: '退货出库', icon: '↪️', disabled: true }
    ]
  },
  transfer: {
    icon: '🔄',
    color: '#fd7e14',
    desc: '仓库间转移',
    subtypes: [
      { code: 'warehouse', name: '仓库移库', icon: '🏢', disabled: true },
      { code: 'location', name: '库位调整', icon: '📌', disabled: true }
    ]
  },
  inventory: {
    icon: '📊',
    color: '#17a2b8',
    desc: '库存盘点 / 调整',
    subtypes: [
      { code: 'count', name: '库存盘点', icon: '🔢' },
      { code: 'adjust', name: '库存调整', icon: '⚙️' }
    ]
  }
};

const typeNames = {
  inbound: '入库',
  outbound: '出库',
  transfer: '移库',
  inventory: '盘点'
};

const selectType = (type) => {
  modelType.value = type;
};

const selectSubtype = (subtype) => {
  const sub = (typeConfig[modelType.value]?.subtypes || []).find(s => s.code === subtype);
  if (sub && sub.disabled) return;
  modelSubtype.value = subtype;
};
</script>

<style scoped>
.type-selector {
  margin-bottom: 15px;
}

.type-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  margin-bottom: 15px;
}

.type-card {
  background: white;
  padding: 15px 10px;
  border-radius: 12px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  border: 2px solid transparent;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.type-card.active {
  transform: scale(1.02);
  background: #f8f9fa;
}

.type-icon {
  font-size: 32px;
  margin-bottom: 8px;
}

.type-title {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 4px;
}

.type-desc {
  font-size: 11px;
  color: #6c757d;
}

.subtype-bar {
  background: white;
  padding: 15px;
  border-radius: 12px;
  margin-bottom: 15px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.subtype-title {
  font-size: 13px;
  color: #6c757d;
  margin-bottom: 10px;
  font-weight: 500;
}

.subtype-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.subtype-btn {
  padding: 10px 20px;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 25px;
  font-size: 14px;
  color: #495057;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.subtype-btn.active {
  background: #007bff;
  color: white;
  border-color: #007bff;
}

.subtype-btn.disabled {
  opacity: 0.45;
  cursor: not-allowed;
  background: #f1f3f5;
  color: #adb5bd;
}

.subtype-tag {
  font-size: 11px;
  padding: 1px 6px;
  border-radius: 8px;
  background: #e9ecef;
  color: #868e96;
}

.subtype-icon {
  font-size: 16px;
}

.subtype-name {
  font-weight: 500;
}
</style>